import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  ArrowDownTrayIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

const InstallBanner = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  background: ${props => props.theme.colors.gradient};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: ${props => props.theme.zIndex.modal};
  backdrop-filter: blur(10px);
  
  @media (min-width: 768px) {
    display: none; /* Hide on desktop */
  }
`;

const InstallContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  color: white;
`;

const InstallIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const InstallText = styled.div`
  flex: 1;
`;

const InstallTitle = styled.div`
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes.md};
  margin-bottom: 2px;
`;

const InstallSubtitle = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  opacity: 0.9;
`;

const InstallActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const InstallButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.borderRadius.md};
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const CloseButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  color: white;
  padding: ${props => props.theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      // Check if running in standalone mode (PWA is installed)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        console.log('🟢 App is already installed (standalone mode)');
        setIsInstalled(true);
        setShowPrompt(false);
        return true;
      }
      
      // Check if running in fullscreen mode (some Android browsers)
      if (window.matchMedia('(display-mode: fullscreen)').matches) {
        console.log('🟢 App is already installed (fullscreen mode)');
        setIsInstalled(true);
        setShowPrompt(false);
        return true;
      }
      
      // Check for iOS standalone mode
      if (window.navigator.standalone === true) {
        console.log('🟢 App is already installed (iOS standalone)');
        setIsInstalled(true);
        setShowPrompt(false);
        return true;
      }
      
      console.log('🔵 App is not installed');
      return false;
    };

    // Check if mobile device
    const checkIfMobile = () => {
      const userAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const screenSize = window.innerWidth <= 768;
      const isMobileDevice = userAgent || screenSize;
      console.log('📱 Mobile detection:', { userAgent, screenSize, isMobile: isMobileDevice });
      setIsMobile(isMobileDevice);
      return isMobileDevice;
    };

    // Check if we should show the prompt based on timing
    const shouldShowPrompt = () => {
      const lastShown = localStorage.getItem('installPromptLastShown');
      const dismissCount = parseInt(localStorage.getItem('installPromptDismissCount') || '0');
      
      if (!lastShown) {
        // First time visitor - show immediately
        return true;
      }

      const daysSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      
      // Show again based on dismiss count:
      // - Dismissed 0-2 times: show every 3 days
      // - Dismissed 3-5 times: show every 7 days  
      // - Dismissed 6+ times: show every 14 days
      let daysToWait = 3;
      if (dismissCount >= 6) {
        daysToWait = 14;
      } else if (dismissCount >= 3) {
        daysToWait = 7;
      }

      return daysSinceLastShown >= daysToWait;
    };

    // Handle display mode changes (when app becomes standalone)
    const handleDisplayModeChange = () => {
      console.log('🔄 Display mode changed, checking install status');
      checkIfInstalled();
    };

    const isAlreadyInstalled = checkIfInstalled();
    const isMobileDevice = checkIfMobile();

    // Only show on mobile devices, when not installed, for unauthenticated users, and based on timing
    if (!isMobileDevice || isAlreadyInstalled || isAuthenticated || !shouldShowPrompt()) {
      console.log('🚫 Not showing prompt:', { 
        isMobile: isMobileDevice, 
        isInstalled: isAlreadyInstalled, 
        isAuthenticated, 
        shouldShow: shouldShowPrompt()
      });
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('🎯 beforeinstallprompt event fired!');
      // Prevent the mini-infobar from appearing
      e.preventDefault();
      // Save the event for later use
      setDeferredPrompt(e);
      
      // Show our custom install prompt after a short delay
      setTimeout(() => {
        if (isMobileDevice && !isInstalled && !isAuthenticated && shouldShowPrompt()) {
          console.log('📢 Showing install prompt');
          setShowPrompt(true);
          // Mark as shown
          localStorage.setItem('installPromptLastShown', Date.now().toString());
        }
      }, 3000); // Show after 3 seconds
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('✅ PWA was installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      // Clear dismiss count since they installed
      localStorage.removeItem('installPromptDismissCount');
      localStorage.removeItem('installPromptLastShown');
      toast.success('SceneFeed installed successfully! 🎵');
    };

    console.log('👂 Adding event listeners for install prompt');
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleDisplayModeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleDisplayModeChange);
    }

    // For testing - show prompt after 5 seconds if no beforeinstallprompt
    const testTimeout = setTimeout(() => {
      if (!deferredPrompt && isMobileDevice && !isInstalled && !isAuthenticated && shouldShowPrompt()) {
        console.log('ℹ️ Note: Native install prompt often doesn\'t work in development mode');
        console.log('ℹ️ For full install experience, deploy to HTTPS server');
        setShowPrompt(true);
        localStorage.setItem('installPromptLastShown', Date.now().toString());
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleDisplayModeChange);
      } else {
        mediaQuery.addListener(handleDisplayModeChange);
      }
      
      clearTimeout(testTimeout);
    };
  }, [isMobile, isInstalled, deferredPrompt, showPrompt, isAuthenticated]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Detect platform for specific instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isChrome = /Chrome/.test(navigator.userAgent);
      const isSafari = /Safari/.test(navigator.userAgent) && !isChrome;
      
      if (isIOS && isSafari) {
        // iOS Safari specific instructions
        toast.success(
          '📱 To install on iOS:\n1. Tap the Share button (⬆️)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add"',
          { duration: 8000 }
        );
      } else if (isAndroid && isChrome) {
        // Android Chrome specific instructions
        toast.success(
          '📱 To install on Android:\n1. Tap the menu (⋮)\n2. Tap "Add to Home screen" or "Install app"\n3. Tap "Add"',
          { duration: 8000 }
        );
      } else {
        // Generic instructions
        toast.success(
          '📱 To install: Look for "Add to Home Screen" or "Install" in your browser menu',
          { duration: 6000 }
        );
      }
      
      console.log('📋 Platform detected:', { isIOS, isAndroid, isChrome, isSafari });
      return;
    }

    try {
      console.log('🚀 Using native install prompt');
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        toast.success('Installing SceneFeed...');
      } else {
        console.log('User dismissed the install prompt');
        toast('You can install anytime from the browser menu');
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Error during installation:', error);
      toast.error('Installation failed. Try using the browser menu.');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Increment dismiss count
    const currentCount = parseInt(localStorage.getItem('installPromptDismissCount') || '0');
    localStorage.setItem('installPromptDismissCount', (currentCount + 1).toString());
    console.log(`📝 Install prompt dismissed ${currentCount + 1} times`);
  };

  return (
    <AnimatePresence>
      {showPrompt && !isInstalled && isMobile && !isAuthenticated && (
        <InstallBanner
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <InstallContent>
            <InstallIcon>
              <ArrowDownTrayIcon />
            </InstallIcon>
            
            <InstallText>
              <InstallTitle>Install SceneFeed</InstallTitle>
              <InstallSubtitle>Get the full app experience</InstallSubtitle>
            </InstallText>
            
            <InstallActions>
              <InstallButton
                onClick={handleInstallClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Install
              </InstallButton>
              
              <CloseButton
                onClick={handleDismiss}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <XMarkIcon />
              </CloseButton>
            </InstallActions>
          </InstallContent>
        </InstallBanner>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt; 